import { useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import { monorepoRoutePaths } from '@apps/admin/route.paths';

import { useRouterHelper, useTranslation } from '@shared/core';
import { useDashboardPreviewTelemetry } from '@apps/admin/routes/DashboardPreview/DashboardPreview.telemetry';
import { useToast } from '@withjoy/joykit';
import { withWindow } from '@shared/utils/withWindow';
import { useUnpublishedWebsiteWarning } from '@apps/admin/routes/WebsiteDesignerV2/components/UnpublishedWebsiteWarning/useUnpublishedWebsiteWarning';
import { useWebsitePublishCapabilityFeature } from '@apps/admin/routes/WebsiteDesignerV2/hooks/useWebsitePublishCapabilityFeature';
import { ShareDialogProps, ShareOption } from './ShareDialog.types';
import { useEventInfo } from '@shared/utils/eventInfo';

export const useShareDialogController = (props: ShareDialogProps) => {
  const { guestSiteLink, eventPassword, eventHandle, onClose } = props;
  const { eventInfo } = useEventInfo();
  const { tq } = useTranslation('shareDialog');
  const { toast } = useToast();
  const routeHelpers = useRouterHelper();
  const telemetry = useDashboardPreviewTelemetry();

  const onCloseDialogHandle = () => {
    telemetry.shareDialogClose();
    onClose();
  };

  const settingsGeneralUrl = useMemo(() => withWindow(global => `${global.location.origin}/${eventHandle}/edit/settings/general`, ''), [eventHandle]);
  const settingsSecurityUrl = useMemo(() => withWindow(global => `${global.location.origin}/${eventHandle}/edit/settings/security`, ''), [eventHandle]);

  const resolveEditLink = useCallback(
    (path: string, isGeneralLink: boolean) => {
      return eventHandle ? (isGeneralLink ? settingsGeneralUrl : settingsSecurityUrl) : path;
    },
    [eventHandle, settingsGeneralUrl, settingsSecurityUrl],
  );

  const { openUnpublishedWebsiteWarning, warningProps: unpublishedWebsiteWarningProps } = useUnpublishedWebsiteWarning({
    context: 'admin',
  });

  const shareOptions: ShareOption[] = [
    {
      title: tq`Joy URL`,
      onCopy: async () => {
        await openUnpublishedWebsiteWarning();
        toast(tq`✨ Joy URL Copied to Clipboard ✨`);
        telemetry.shareDialogCopy('Joy URL');
      },
      text: guestSiteLink,
      editLink: resolveEditLink(routeHelpers.buildPath(monorepoRoutePaths.settingsGeneral), true),
      onEditLinkClick: () => telemetry.shareDialogEdit('Joy URL'),
    },
    {
      title: tq`Event Password`,
      onCopy: () => {
        toast(tq`✨ Event Password Copied to Clipboard ✨`);
        telemetry.shareDialogCopy('Event Password');
      },
      text: eventPassword,
      editLink: resolveEditLink(routeHelpers.buildPath(monorepoRoutePaths.settingsSecurity), false),
      onEditLinkClick: () => telemetry.shareDialogEdit('Event Password'),
    },
  ];

  const guestShareOptions: ShareOption[] = [
    {
      title: tq`Joy URL`,
      onCopy: () => {
        toast(tq`✨ Joy URL Copied to Clipboard ✨`);
        telemetry.shareDialogCopy('Joy URL');
      },
      text: guestSiteLink,
    },
  ];

  const onOpenNewTab = () => {
    telemetry.guestSiteViewOpen();
    withWindow(global => global.open(guestSiteLink, '_blank'));
  };

  const websitePublishCapabilityFeature = useWebsitePublishCapabilityFeature();
  const showPublishSection = !websitePublishCapabilityFeature.eventExposure.isLoading && websitePublishCapabilityFeature.eventExposure.value;
  const isEventPublished = !websitePublishCapabilityFeature.eventPublishedState.isLoading && websitePublishCapabilityFeature.eventPublishedState.value;

  const publishFormik = useFormik({
    initialValues: {
      published: isEventPublished,
    },
    enableReinitialize: true,
    onSubmit: async values => {
      try {
        await websitePublishCapabilityFeature.eventPublishedState.update(values.published);

        const eventId = eventInfo?.eventId || '';

        if (values.published) {
          telemetry.websiteVisibilityPublish(eventId);
        } else {
          telemetry.websiteVisibilityUnpublish(eventId);
        }

        toast(tq`🥳 Your changes have been saved!`);
      } catch (error) {
        toast(tq`😬 Failed to save changes. Please try again.`);
      }
    },
  });

  return {
    shareOptions,
    guestShareOptions,
    onOpenNewTab,
    onCloseDialogHandle,
    unpublishedWebsiteWarningProps,
    publishFormik,
    showPublishSection,
  };
};
